import * as React from 'react';
import { useQuery } from 'react-query';
import {
  useFetchInvoiceIndicators,
} from 'hooks/api/invoices/use-fetch-invoice-indicators';
import {
  useInvoicesTrackingSearchParams,
  type Filters,
} from 'hooks/store/invoices/use-invoices-tracking-search-params';
import { cn } from 'lib/utils';

import {
  PreparingForTransitCard,
  InTransitCard,
  PreparingForDeliveryCard,
  DeliveredCard,
} from './components';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  onSubmitFilters: (filters: Filters) => void;
}

export const InvoiceIndicators = ({
                                    onSubmitFilters,
                                    className,
                                    ...props
                                  }: Props) => {
  const { filters } = useInvoicesTrackingSearchParams({ persisted: true });
  const { fetchInvoiceIndicators } = useFetchInvoiceIndicators();

  const activeStatus = React.useMemo(() => {
    return filters?.indicatorStatus?.length === 1
      ? filters?.indicatorStatus[0]
      : undefined;
  }, [filters?.indicatorStatus]);

  const handleToggleStatus = React.useCallback((status: string) => {
    onSubmitFilters({
      ...filters,
      indicatorStatus: activeStatus === status ? [] : [status],
    });
  }, [activeStatus, filters, onSubmitFilters]);

  const indicatorsQuery = useQuery({
    queryKey: ['fetch-invoice-indicators', filters],
    queryFn: () => {
      return fetchInvoiceIndicators({ filters });
    },
    refetchOnWindowFocus: false,
  });

  return (
    <div
      {...props}
      className={cn(
        'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6',
        className,
      )}
    >
      <PreparingForTransitCard
        indicator={indicatorsQuery?.data?.preparingForTransit}
        loading={indicatorsQuery.isLoading}
        onToggleStatus={handleToggleStatus}
        activeStatus={activeStatus}
      />
      <InTransitCard
        indicator={indicatorsQuery?.data?.inTransit}
        loading={indicatorsQuery.isLoading}
        onToggleStatus={handleToggleStatus}
        activeStatus={activeStatus}
      />
      <PreparingForDeliveryCard
        indicator={indicatorsQuery?.data?.preparingForDelivery}
        loading={indicatorsQuery.isLoading}
        onToggleStatus={handleToggleStatus}
        activeStatus={activeStatus}
      />
      <DeliveredCard
        indicator={indicatorsQuery?.data?.delivered}
        loading={indicatorsQuery.isLoading}
        onToggleStatus={handleToggleStatus}
        activeStatus={activeStatus}
      />
    </div>
  );
};